import type { User } from '@/components/modal/composables/search/adapters/types';
import type { OrganizationMembership } from '@/models/user.models';

/**
 * Domain service for user-specific business logic
 *
 * This service extracts user-related logic from UI components
 * to maintain separation of concerns and enable reusability.
 */
export class UserService {
  /**
   * Get full display name for a user
   * @param user - The user object
   * @returns Full name or email as fallback
   */
  static getDisplayName(user: User): string {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) {
      return user.firstName;
    }
    if (user.lastName) {
      return user.lastName;
    }
    return user.email || 'Unknown User';
  }

  /**
   * Get user initials for avatar display
   * @param user - The user object
   * @returns Two-character initials
   */
  static getInitials(user: User): string {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user.firstName) {
      return user.firstName.slice(0, 2).toUpperCase();
    }
    if (user.lastName) {
      return user.lastName.slice(0, 2).toUpperCase();
    }
    if (user.email) {
      return user.email.slice(0, 2).toUpperCase();
    }
    return 'UN';
  }

  /**
   * Check if user is active
   * @param user - The user object
   * @returns Whether the user is active
   */
  static isActive(user: User): boolean {
    // Check various status fields that might indicate inactive users
    if ('status' in user) {
      return (user as { status: string }).status === 'active';
    }
    if ('active' in user) {
      return (user as unknown as { active: boolean }).active === true;
    }
    if ('deactivated' in user) {
      return (user as unknown as { deactivated: boolean }).deactivated !== true;
    }
    // Default to active if no status field
    return true;
  }

  /**
   * Check if user is an admin
   * @param user - The user object
   * @returns Whether the user has admin privileges
   */
  static isAdmin(user: User): boolean {
    if ('role' in user) {
      const role = (user as { role: string }).role;
      return role === 'admin' || role === 'owner' || role === 'superadmin';
    }
    if ('isAdmin' in user) {
      return (user as unknown as { isAdmin: boolean }).isAdmin === true;
    }
    if ('permissions' in user) {
      const permissions = (user as { permissions: unknown }).permissions;
      return Array.isArray(permissions) && permissions.includes('admin');
    }
    return false;
  }

  /**
   * Sort users by name
   * @param users - List of users to sort
   * @param order - Sort order
   * @returns Sorted list of users
   */
  static sortByName(users: User[], order: 'asc' | 'desc' = 'asc'): User[] {
    const sorted = [...users].sort((a, b) => {
      const nameA = this.getDisplayName(a).toLowerCase();
      const nameB = this.getDisplayName(b).toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return order === 'desc' ? sorted.reverse() : sorted;
  }

  /**
   * Filter users by active status
   * @param users - List of users to filter
   * @param activeOnly - Whether to show only active users
   * @returns Filtered list of users
   */
  static filterByStatus(users: User[], activeOnly: boolean = true): User[] {
    if (!activeOnly) return users;
    return users.filter((user) => this.isActive(user));
  }

  /**
   * Search users by name or email
   * @param users - List of users to search
   * @param query - Search query
   * @returns Filtered list of users matching query
   */
  static searchUsers(users: User[], query: string): User[] {
    if (!query) return users;

    const lowerQuery = query.toLowerCase();
    return users.filter((user) => {
      const displayName = this.getDisplayName(user).toLowerCase();
      const email = (user.email || '').toLowerCase();
      return displayName.includes(lowerQuery) || email.includes(lowerQuery);
    });
  }

  /**
   * Group users by role
   * @param users - List of users to group
   * @returns Users grouped by role
   */
  static groupByRole(users: User[]): Record<string, User[]> {
    return users.reduce((groups, user) => {
      const role =
        ('role' in user ? (user as { role: string }).role : null) || 'member';
      if (!groups[role]) {
        groups[role] = [];
      }
      groups[role].push(user);
      return groups;
    }, {} as Record<string, User[]>);
  }

  /**
   * Get user avatar URL or generate default
   * @param user - The user object
   * @returns Avatar URL or null for default
   */
  static getAvatarUrl(user: User): string | null {
    if ('avatar' in user && user.avatar) {
      return user.avatar as string;
    }
    if ('avatarUrl' in user && user.avatarUrl) {
      return user.avatarUrl as string;
    }
    if ('profileImage' in user && user.profileImage) {
      return user.profileImage as string;
    }
    // Return null to use default avatar component
    return null;
  }

  /**
   * Check if user has specific permission
   * @param user - The user object
   * @param permission - Permission to check
   * @returns Whether user has the permission
   */
  static hasPermission(user: User, permission: string): boolean {
    if ('permissions' in user) {
      const permissions = (user as { permissions: unknown }).permissions;
      if (Array.isArray(permissions)) {
        return permissions.includes(permission);
      }
    }
    // Admin users typically have all permissions
    return this.isAdmin(user);
  }

  /**
   * Type guard to check if item is OrganizationMembership
   * @param item - Item to check
   * @returns True if item is OrganizationMembership
   */
  private static isOrganizationMembership(
    item: User | OrganizationMembership
  ): item is OrganizationMembership {
    return 'userId' in item;
  }

  /**
   * Get ID from a user or organization membership object
   * @param item - User or OrganizationMembership object
   * @returns The user ID
   */
  static getItemId(item: User | OrganizationMembership): string {
    if (this.isOrganizationMembership(item)) {
      return item.userId;
    }
    return item.id;
  }

  /**
   * Find user or organization membership in cache by ID
   * @param cache - Cache to search (User[] or OrganizationMembership[])
   * @param id - User ID to find
   * @returns Found item or undefined
   */
  static findInCache<T extends User | OrganizationMembership>(
    cache: T[],
    id: string
  ): T | undefined {
    return cache.find((item) => {
      if (this.isOrganizationMembership(item)) {
        return item.userId === id;
      }
      return item.id === id;
    });
  }

  /**
   * Get user's team memberships
   * @param user - The user object
   * @returns Array of team IDs or empty array
   */
  static getTeamIds(user: User): string[] {
    if ('teams' in user) {
      const teams = (user as { teams: unknown }).teams;
      if (Array.isArray(teams)) {
        return teams.map((t) =>
          typeof t === 'string' ? t : (t as { id: string }).id
        );
      }
    }
    if ('teamIds' in user) {
      const teamIds = (user as unknown as { teamIds: unknown }).teamIds;
      if (Array.isArray(teamIds)) {
        return teamIds as string[];
      }
    }
    return [];
  }
}

// Export as default for easier imports
export default UserService;
